// CCC-001 CC-S1 Fase C-prep (Calvin 2026-06-10 "seguir") — inline module de
// broadcast.html extraído para arquivo externo (CSP script-src-elem sem
// 'unsafe-inline'). Comportamento idêntico: carrega modelo via share_code
// da URL, i18n da página, polling de heartbeat do radar. Página pública
// standalone.
// ================================================================
// BROADCAST PAGE — Carrega modelo via share_code da URL
// Acesso publico: ecbyts.com/broadcast?id=XXXXXXXX
// As outras duas formas de link público do repositório — `?code=` (Radar/
// compartilhamento) e `?share=` (stakeholder share) — caem no mesmo código.
// ================================================================
//
// i18n (FINDING-008, UI/UX Audit 2026-06-04, claude - ui):
// página standalone — sem dependência do sistema i18n principal do app.
//
// Calvin 2026-09-28 ("ecbyts.com deve sempre aparecer para o usuário com o idioma
// configurado no navegador dele") + esta tarefa: o dicionário de 3 idiomas que
// vivia EMBUTIDO aqui saiu de cena — a página passou a usar os 16 JSONs de
// `app/assets/i18n/broadcast/<locale>.json` (~2 KB cada), os mesmos 16 idiomas
// da landing (app/js/boot/landing-init.js) e das páginas legais.
//
// COMO FUNCIONA:
//   1. decide o idioma com a MESMA regra de `detectLanguage` de landing-init.js /
//      references-init.js / i18n-early.js: escolha salva (`ecbyts_lang` do app e
//      depois `ecbyts-language`, a chave do aviso de privacidade) → `navigator.languages`
//      (alias de código curto pt→pt-BR/en→en-US/es→es-LA + prefixo de 2 letras
//      fr-CA→fr-FR) → `navigator.language` → reserva internacional en-US;
//   2. baixa o JSON do idioma ANTES do primeiro render que usa `_t()` — senão o
//      payload público (card, status, camada leiga) sairia sem tradução;
//   3. aplica em `[data-i18n]` (o rodapé da página) e atualiza <html lang> e dir
//      (ar-SA é RTL);
//   4. traduz por `_t(chave)` tudo o que é montado por template.
//
// FALLBACK (fail-safe, nunca chave crua e nunca tela vazia):
//   - fetch do idioma falhou/404/corpo vazio → dicionário EN embutido (I18N_FALLBACK);
//   - chave ausente no JSON do idioma → a mesma reserva EN;
//   - chave ausente também na reserva → o TEXTO BASE do HTML já cobre o rodapé e o
//     resto da página segue legível; `_t` devolve '' (nunca o identificador da chave).
//
// A decisão de idioma NÃO persiste nada: traduzir a primeira pintura com o idioma
// do navegador não é escolha explícita do usuário (mesma regra da landing).

// Lista fechada dos idiomas COM tradução em app/assets/i18n/broadcast/ (16 arquivos).
const SUPPORTED = [
    'pt-BR',
    'en-US',
    'es-LA',
    'zh-CN',
    'fr-FR',
    'de-DE',
    'ja-JP',
    'it-IT',
    'ko-KR',
    'ru-RU',
    'id-ID',
    'hi-IN',
    'tr-TR',
    'pl-PL',
    'sv-SE',
    'ar-SA',
];

const STORAGE_KEY = 'ecbyts_lang';
// Chave do aviso de privacidade: as páginas públicas gravam nela a escolha manual.
const PUBLIC_STORAGE_KEY = 'ecbyts-language';
// Alias de código curto — 'pt' e 'en' sozinhos NÃO são códigos de arquivo aqui.
const ALIAS = { en: 'en-US', pt: 'pt-BR', es: 'es-LA' };
// Único idioma RTL da lista: <html dir="rtl"> é obrigatório para o árabe.
const RTL = ['ar-SA'];
const BASE_LANG = 'pt-BR';
// Reserva internacional — espelha `detectLanguage` (landing/references) e
// `DEFAULT_LANGUAGE` de app/js/boot/i18n-early.js.
const FALLBACK_LANG = 'en-US';

// Reserva embutida (EN) — o antigo dicionário `en` desta página, agora fonte de
// emergência: entra quando o fetch do idioma falha e cobre qualquer chave que o
// JSON do idioma não tenha. Mantém os 40 rótulos + o rodapé usados pela página.
const I18N_FALLBACK = Object.freeze({
    loading: 'Loading broadcast...',
    noBroadcastId: 'No broadcast ID',
    noBroadcastIdHint: 'Add ?id=XXXXXXXX to the URL.',
    notFound: 'Broadcast not found',
    notFoundHint: 'This broadcast may have been turned off or expired.',
    errorLoading: 'Error loading broadcast',
    offlineTitle: 'Broadcast Offline',
    offlineHint: 'This broadcast is currently offline. The owner may have closed their session.',
    goToEcbyts: 'Go to ecbyts',
    statusLive: 'LIVE',
    statusOffline: 'OFFLINE',
    untitled: 'Untitled Model',
    byOwner: 'by',
    elements: 'Elements',
    campaigns: 'Campaigns',
    active: 'Active',
    visibleFamilies: 'Visible Families',
    availableSections: 'Available Sections',
    openInEcbyts: 'Open in ecbyts',
    copyLink: 'Copy Link',
    copied: 'Copied!',
    timeNow: 'now',
    unknown: 'Unknown',
    // Camada leiga (P5-2) — contexto em linguagem simples para o público.
    'lay.whatis.title': 'What am I seeing?',
    'lay.whatis.body':
        'This is a public summary of an environmental monitoring site. Technical data follow Brazilian standards (CONAMA/CETESB). To learn whether there is any restriction on using the water or soil in this area, contact the publisher or your local environmental agency.',
    'lay.safety.within.label': 'Within reference standards',
    'lay.safety.within.explain': 'Measurements are within the regulatory reference values.',
    'lay.safety.monitoring.label': 'Under monitoring',
    'lay.safety.monitoring.explain': 'Some measurements require continued monitoring.',
    'lay.safety.exceeded.label': 'Above the limit — attention required',
    'lay.safety.exceeded.explain': 'One or more measurements exceed the intervention threshold.',
    'lay.safety.unknown.label': 'Status not shown here',
    'lay.safety.unknown.explain':
        'The compliance status is not included in this public view. Ask the publisher for details.',
    // Expirado / não encontrado acionável (P3-7).
    'lay.expired.title': 'This broadcast has expired',
    'lay.expired.hint': 'This broadcast link expired or was turned off. Ask the publisher for a new link.',
    'lay.notFound.title': 'Broadcast not found',
    'lay.notFound.hint': 'This link is invalid or was removed. Ask the publisher for a current link.',
    // Rodapé (chaves equivalentes nos 16 JSONs: footer.terms/privacy/tagline).
    'footer.terms': 'Terms of Use',
    'footer.privacy': 'Privacy Policy',
    'footer.tagline': 'Environmental Digital Twins',
});

// Dicionário corrente: começa na reserva EN e é TROCADO pelo JSON do idioma assim
// que ele chega. `_t` é uma função (não uma constante), então todo render posterior
// à troca já sai traduzido — inclusive um segundo render disparado por erro.
let _dict = I18N_FALLBACK;
let _booted = false;

// Contêineres do render, preenchidos no boot: `_showError`/`_showExpired` são
// chamados logo depois do primeiro `await`, então a referência precisa existir
// no escopo do módulo (com `const` dentro de _boot ela cairia em TDZ).
let mainEl = null;
let statusEl = null;

/**
 * Resolve um código bruto para um idioma com arquivo em `SUPPORTED`.
 * @param {string} code Código vindo do navegador ou do armazenamento.
 * @returns {string} Idioma suportado, ou '' quando não há casamento.
 */
function _resolveLocale(code) {
    if (!code) return '';
    const raw = String(code).trim();
    if (!raw) return '';
    const lowered = raw.toLowerCase();
    const aliased = Object.prototype.hasOwnProperty.call(ALIAS, lowered) ? ALIAS[lowered] : raw;
    if (SUPPORTED.indexOf(aliased) >= 0) return aliased;
    // Prefixo de 2 letras: fr-CA→fr-FR, en-GB→en-US, pt-PT→pt-BR, es-MX→es-LA.
    const prefix = lowered.slice(0, 2);
    for (let i = 0; i < SUPPORTED.length; i++) {
        if (SUPPORTED[i].toLowerCase().indexOf(prefix) === 0) return SUPPORTED[i];
    }
    return '';
}

/**
 * Mesma regra de `detectLanguage` em app/js/boot/landing-init.js (e de
 * references-init.js / i18n-early.js): escolha salva → `navigator.languages`
 * (com alias e prefixo) → `navigator.language` → reserva internacional en-US.
 * @param {{getItem: (k: string) => string|null}|null} storage
 * @param {object|null} nav Objeto navigator (ou similar).
 * @returns {string} Um dos idiomas de `SUPPORTED`.
 */
function detectLanguage(storage, nav) {
    let saved = '';
    try {
        saved = (storage && (storage.getItem(STORAGE_KEY) || storage.getItem(PUBLIC_STORAGE_KEY))) || '';
    } catch (e) {
        saved = '';
    }
    // Só a escolha EXPLÍCITA vale: um valor fora da lista cai na detecção do navegador.
    if (saved && SUPPORTED.indexOf(saved) >= 0) return saved;

    const candidates =
        nav && nav.languages && nav.languages.length ? nav.languages : nav && nav.language ? [nav.language] : [];
    for (let i = 0; i < candidates.length; i++) {
        const resolved = _resolveLocale(candidates[i]);
        if (resolved) return resolved;
    }
    return FALLBACK_LANG;
}

/**
 * Traduz uma chave i18n. Nunca devolve o identificador da chave: idioma → reserva
 * EN → '' (a base estática do HTML permanece visível).
 * @param {string} key
 * @returns {string}
 */
function _t(key) {
    const value = _dict[key];
    if (typeof value === 'string' && value) return value;
    const fallback = I18N_FALLBACK[key];
    return typeof fallback === 'string' && fallback ? fallback : '';
}

/**
 * Caminho do bundle de idiomas, derivado da localização DESTE módulo.
 * O deploy empacota `js/boot/broadcast-init.js` num arquivo com hash ao lado dele
 * (scripts/build-evolution/bundle-deploy.mjs), então `./assets/...` (relativo ao
 * DOCUMENTO) apontaria para `/js/boot/assets/...` — 404. Tirando o sufixo
 * `js/boot/<arquivo>.js` da própria URL sobra a base do site, em dev (`/`) e no
 * bundle.
 * @returns {string}
 */
function _bundleBasePath() {
    // Valor de reserva: raiz do site (é onde a página broadcast é servida).
    let root = '/';
    try {
        const pathname = new URL(import.meta.url).pathname;
        // O prefixo de disco (Windows) fica ANTES do sufixo conhecido — cortá-lo
        // mantém o caminho servível.
        const cut = pathname.lastIndexOf('/js/boot/');
        if (cut >= 0) root = pathname.slice(0, pathname.indexOf('/js/boot/')) + '/';
    } catch (e) {
        console.debug('[broadcast:i18n] base do bundle', e?.message || e);
    }
    return root;
}

const ASSET_PATH = _bundleBasePath() + 'assets/i18n/broadcast/';

/**
 * Lê o dicionário do idioma. Caminho absoluto (raiz do site) com um segundo
 * disparo relativo ao documento, que é a forma literal do padrão
 * `references-init.js` no repositório — cobre servir o app a partir de um
 * subdiretório, onde a raiz do site não é a raiz do app.
 * @param {string} lang
 * @returns {Promise<object|null>} Dicionário ou null quando indisponível.
 */
async function _loadDictionary(lang) {
    if (typeof fetch !== 'function') return null;
    const candidates = [ASSET_PATH + lang + '.json', './assets/i18n/broadcast/' + lang + '.json'];
    for (let i = 0; i < candidates.length; i++) {
        try {
            const response = await fetch(candidates[i]);
            if (!response || !response.ok) continue;
            const data = await response.json();
            // Corpo vazio/ inválido não substitui a reserva por um dicionário inútil.
            if (data && typeof data === 'object' && Object.keys(data).length > 0) return data;
        } catch (e) {
            console.warn('[broadcast:i18n]', candidates[i], e && e.message ? e.message : e);
        }
    }
    return null;
}

/**
 * Aplica o dicionário nos nós estáticos marcados com `data-i18n` (rodapé) e ajusta
 * <html lang>/<html dir>. Chamada de novo quando o JSON chega — o texto base do
 * HTML cobre a janela entre a primeira pintura e o dicionário.
 * @param {object|null} dict
 * @param {string} lang
 */
function _applyStaticI18n(dict, lang) {
    const root = document.documentElement;
    if (root) {
        root.setAttribute('lang', lang);
        root.setAttribute('dir', RTL.indexOf(lang) >= 0 ? 'rtl' : 'ltr');
    }
    if (!dict) return;
    const nodes = document.querySelectorAll('[data-i18n]');
    for (let i = 0; i < nodes.length; i++) {
        const value = dict[nodes[i].getAttribute('data-i18n')];
        // Sem valor no idioma: mantém o texto base (nunca escreve a chave na tela).
        if (typeof value !== 'string' || !value) continue;
        // `<meta>` não tem texto: a descrição que aparece na busca e na prévia de link vive
        // no atributo `content` — textContent ali não faria nada e a página continuaria no
        // idioma do arquivo (mesmo defeito provado nas páginas de curso, 2026-09-30).
        if (nodes[i].tagName === 'META') {
            nodes[i].setAttribute('content', value);
        } else {
            nodes[i].textContent = value;
        }
    }
}

let _lang = FALLBACK_LANG;

/**
 * Rótulo do spinner no idioma corrente. Chamado antes E depois de o dicionário
 * chegar: o primeiro tiro pode sair na reserva embutida (quando o fetch ainda não
 * voltou) e o segundo corrige para o idioma do usuário.
 */
function _paintLoading() {
    const el = document.getElementById('bc-loading-msg');
    if (el) el.textContent = _t('loading');
}

/**
 * Ordem obrigatória: idioma → dicionário → primeiro render que usa `_t()`.
 * O `<html lang>`/`dir` saem já na primeira linha, antes de qualquer rede.
 */
async function _boot() {
    if (_booted) return;
    _booted = true;

    let storage = null;
    try {
        storage = window.localStorage;
    } catch (e) {
        storage = null;
    }
    if (!storage && typeof localStorage !== 'undefined') storage = localStorage;

    _lang = detectLanguage(storage, typeof navigator !== 'undefined' ? navigator : null);

    // Primeira pintura: <html lang>/dir, texto base do HTML (o rodapé em pt
    // continua legível se a rede falhar) e o rótulo de carregamento já traduzido
    // quando o dicionário for a reserva embutida.
    _applyStaticI18n(null, _lang);
    _paintLoading();

    const dict = await _loadDictionary(_lang);
    if (dict) _dict = dict;
    else console.warn('[broadcast:i18n] dicionário indisponível — usando a reserva embutida (EN)');

    // Com o dicionário em mãos: reaplica o rótulo de carregamento no idioma certo e
    // o texto base do rodapé. Sem dicionário, o apply(null) de cima já garantiu
    // lang/dir e a base legível.
    _paintLoading();
    _applyStaticI18n(dict, _lang);

    const params = new URLSearchParams(window.location.search);
    const shareCode = params.get('id') || params.get('code') || params.get('share');

    mainEl = document.getElementById('bc-main');
    statusEl = document.getElementById('bc-status');

    if (!shareCode) {
        _showError(_t('noBroadcastId'), _t('noBroadcastIdHint'));
    } else {
        await _loadBroadcast(shareCode);
    }
}

// A decisão de idioma roda junto com o resto do boot; o contrato público
// permite a um teste executar a MESMA decisão que o navegador roda (padrão
// window.__ecbytsLandingLang de landing-init.js).
window.__ecbytsBroadcastI18n = {
    lang: () => _lang,
    detectLanguage: detectLanguage,
    resolveLocale: _resolveLocale,
    supported: SUPPORTED,
    fallback: I18N_FALLBACK,
    text: _t,
    applyStatic: _applyStaticI18n,
};

// FRIEND-SCORE-001 ui/broadcast (P5-2 + P3-7): camada leiga + mensagens
// acionáveis. Pure-logic testável em core/radar/_broadcastLayContextPure.
let _lay = null;
try {
    _lay = await import('../core/radar/_broadcastLayContextPure.js');
} catch (e) {
    console.debug('[broadcast:lay]', e?.message || e);
}

await _boot();

async function _loadBroadcast(code) {
    try {
        // Inicializar Supabase client
        const configResp = await fetch('./js/supabase-config.js');
        if (!configResp.ok) throw new Error('Config not available');

        // Dynamic import supabase-config para obter URL e key.
        // PATH CORRIGIDO (SPEC_BUILD_EVOLUTION Fases 1-2, gate dangling-imports):
        // era './js/supabase-config.js', que a partir de js/boot/ resolve para
        // js/boot/js/supabase-config.js — INEXISTENTE. O import 404-ava, o catch
        // engolia e a página broadcast degradava muda. O fetch acima é relativo ao
        // DOCUMENTO (página na raiz) e continua './js/...'; o import é relativo ao
        // MÓDULO, portanto '../supabase-config.js'.
        const configModule = await import('../supabase-config.js');
        const { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } = configModule;

        if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
            throw new Error('Supabase config missing');
        }

        // Carregar Supabase client via CDN
        const { createClient } = await import('https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/+esm');
        const client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

        // 1. Buscar radar entry por share_code
        const { data: radar, error: radarErr } = await client
            .from('model_radar')
            .select(
                'id, model_id, profile_id, visibility, share_code, activated_at, last_heartbeat, is_persistent, owner_id',
            )
            .eq('share_code', code)
            .is('deactivated_at', null)
            .single();

        if (radarErr || !radar) {
            // P3-7 irmã: caminho acionável ("peça um link atual") em vez
            // da mensagem genérica.
            if (_lay) _renderAction(_lay.buildNotFoundActionVM(), '');
            else _showError(_t('notFound'), _t('notFoundHint'));
            return;
        }

        // Verificar heartbeat para free tier
        if (!radar.is_persistent) {
            const age = Date.now() - new Date(radar.last_heartbeat).getTime();
            if (age > 120000) {
                _showExpired();
                return;
            }
        }

        // 2. Buscar profile
        let profile = null;
        if (radar.profile_id) {
            const { data: bp } = await client
                .from('radar_broadcast_profiles')
                .select('name, visible_families, visible_sections, hidden_fields')
                .eq('id', radar.profile_id)
                .single();
            profile = bp;
        }

        // 3. Buscar modelo (metadata apenas — sem model_data nesta pagina)
        const { data: model } = await client
            .from('models')
            .select('id, name, description, element_count, campaign_count')
            .eq('id', radar.model_id)
            .single();

        // 4. Buscar owner
        const { data: owner } = await client.from('profiles').select('display_name').eq('id', radar.owner_id).single();

        // 5. Render
        _showBroadcast({
            radar,
            model: model || {},
            profile: profile || {},
            ownerName: owner?.display_name || _t('unknown'),
        });
    } catch (e) {
        console.error('[Broadcast]', e);
        // A mensagem técnica também é texto do usuário: traduz o título e mantém
        // o detalhe do erro (legível) ao lado.
        _showError(_t('errorLoading'), e && e.message ? e.message : String(e));
    }
}

function _showBroadcast({ radar, model, profile, ownerName }) {
    // Status badge
    statusEl.textContent = _t('statusLive');
    statusEl.className = 'bc-live-badge bc-live-badge-active';

    // Families and sections
    const families = profile.visible_families || [];
    const sections = profile.visible_sections || [];

    // Count filtered elements
    const elementCount = model.element_count || 0;
    const campaignCount = model.campaign_count || 0;

    // Time ago
    const activeFor = _timeAgo(radar.activated_at);

    mainEl.innerHTML = `
                <div class="bc-card">
                    <div class="bc-card-header">
                        <h1>${_esc(model.name || _t('untitled'))}</h1>
                        <div class="bc-card-owner">${_esc(_t('byOwner'))} ${_esc(ownerName)}</div>
                        ${profile.name ? `<span class="bc-card-profile">${_esc(profile.name)}</span>` : ''}
                    </div>

                    ${model.description ? `<div class="bc-card-description">${_esc(model.description)}</div>` : ''}

                    ${_layContextHtml(radar.lay_safety_status)}

                    <div class="bc-stats">
                        <div class="bc-stat">
                            <div class="bc-stat-value">${elementCount}</div>
                            <div class="bc-stat-label">${_esc(_t('elements'))}</div>
                        </div>
                        <div class="bc-stat">
                            <div class="bc-stat-value">${campaignCount}</div>
                            <div class="bc-stat-label">${_esc(_t('campaigns'))}</div>
                        </div>
                        <div class="bc-stat">
                            <div class="bc-stat-value">${activeFor}</div>
                            <div class="bc-stat-label">${_esc(_t('active'))}</div>
                        </div>
                    </div>

                    ${
                        families.length > 0
                            ? `
                        <div class="bc-matrix">
                            <h3>${_esc(_t('visibleFamilies'))}</h3>
                            <div class="bc-tags">
                                ${families.map((f) => `<span class="bc-tag">${_esc(f)}</span>`).join('')}
                            </div>
                        </div>
                    `
                            : ''
                    }

                    ${
                        sections.length > 0
                            ? `
                        <div class="bc-matrix">
                            <h3>${_esc(_t('availableSections'))}</h3>
                            <div class="bc-tags">
                                ${sections.map((s) => `<span class="bc-tag">${_esc(s)}</span>`).join('')}
                            </div>
                        </div>
                    `
                            : ''
                    }

                    <div class="bc-actions">
                        <a href="./?broadcast=${radar.share_code}" class="bc-btn bc-btn-primary">
                            ${_esc(_t('openInEcbyts'))}
                        </a>
                        <button type="button" id="bc-copy" class="bc-btn bc-btn-secondary" data-copied-label="${_esc(_t('copied'))}">
                            ${_esc(_t('copyLink'))}
                        </button>
                    </div>
                </div>
            `;
    _wireCopyButton();
}

// O handler inline (`onclick=`) saiu: a CSP de produção declara script-src-attr
// 'none', então ele NUNCA executava — o botão de copiar link estava morto. O
// listener é ligado por código depois do render (padrão CSP do repositório),
// mantendo o mesmo efeito: copia a URL atual e troca o rótulo por "Copiado!".
function _wireCopyButton() {
    const button = document.getElementById('bc-copy');
    if (!button || typeof button.addEventListener !== 'function') return;
    button.addEventListener('click', () => {
        const copiedLabel =
            button.dataset && button.dataset.copiedLabel
                ? button.dataset.copiedLabel
                : button.getAttribute('data-copied-label');
        navigator.clipboard.writeText(window.location.href).then(() => {
            button.textContent = copiedLabel;
        });
    });
}

// P5-2: bloco leigo "o que é isto?" + selo de situação (quando houver
// status; ausente → orientação neutra, nunca afirma segurança sem dado).
function _layContextHtml(rawStatus) {
    if (!_lay) return '';
    const vm = _lay.buildLaySafetyVM(rawStatus);
    const toneClass = `bc-lay-safety bc-lay-safety--${vm.tone}`;
    return `
                <div class="bc-lay">
                    <div class="${toneClass}">
                        <span class="bc-lay-dot" aria-hidden="true"></span>
                        <div>
                            <div class="bc-lay-label">${_esc(_t(vm.labelKey))}</div>
                            <div class="bc-lay-explain">${_esc(_t(vm.explainKey))}</div>
                        </div>
                    </div>
                    <details class="bc-lay-whatis">
                        <summary>${_esc(_t(_lay.LAY_WHATIS_TITLE_KEY))}</summary>
                        <p>${_esc(_t(_lay.LAY_WHATIS_BODY_KEY))}</p>
                    </details>
                </div>`;
}

// P3-7: mensagem acionável reutilizável (expirado / não encontrado).
function _renderAction(vm, badgeLabel) {
    if (badgeLabel) {
        statusEl.textContent = badgeLabel;
        statusEl.className = 'bc-live-badge bc-live-badge-expired';
    } else {
        statusEl.textContent = '';
    }
    mainEl.innerHTML = `
                <div class="bc-error">
                    <h2>${_esc(_t(vm.titleKey))}</h2>
                    <p>${_esc(_t(vm.hintKey))}</p>
                    <a href="./" class="bc-btn bc-btn-primary">${_esc(_t(vm.ctaKey))}</a>
                </div>
            `;
}

function _showExpired() {
    if (_lay) return _renderAction(_lay.buildExpiredActionVM(), _t('statusOffline'));
    // Fallback se o pure não carregou.
    statusEl.textContent = _t('statusOffline');
    statusEl.className = 'bc-live-badge bc-live-badge-expired';
    mainEl.innerHTML = `
                <div class="bc-error">
                    <h2>${_esc(_t('offlineTitle'))}</h2>
                    <p>${_esc(_t('offlineHint'))}</p>
                    <a href="./">${_esc(_t('goToEcbyts'))}</a>
                </div>
            `;
}

function _showError(title, message) {
    statusEl.textContent = '';
    mainEl.innerHTML = `
                <div class="bc-error">
                    <h2>${_esc(title)}</h2>
                    <p>${_esc(message)}</p>
                    <a href="./">${_esc(_t('goToEcbyts'))}</a>
                </div>
            `;
}

function _esc(s) {
    if (!s) return '';
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
}

function _timeAgo(dateStr) {
    if (!dateStr) return '-';
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 1) return _t('timeNow');
    if (mins < 60) return mins + 'm';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h';
    return Math.floor(hrs / 24) + 'd';
}
